import { useState, useCallback } from "react";
import {
  MasterComplianceCalendarEntity,
  GroupedCalendarEntries,
} from "../../domain/entities/MasterComplianceCalendarEntity";
import { ComplianceRepositoryImpl } from "../../data/repositories/ComplianceRepositoryImpl";
import {
  GetCalendarEntriesUseCase,
  GetCalendarEntriesGroupedUseCase,
  CreateCalendarEntryUseCase,
  UpdateCalendarEntryUseCase,
  DeleteCalendarEntryUseCase,
} from "../../domain/use-cases/calendar";

// Create repository instance (singleton)
const complianceRepository = new ComplianceRepositoryImpl();

// Create use cases
const getCalendarEntriesUseCase = new GetCalendarEntriesUseCase(complianceRepository);
const getCalendarEntriesGroupedUseCase = new GetCalendarEntriesGroupedUseCase(complianceRepository);
const createCalendarEntryUseCase = new CreateCalendarEntryUseCase(complianceRepository);
const updateCalendarEntryUseCase = new UpdateCalendarEntryUseCase(complianceRepository);
const deleteCalendarEntryUseCase = new DeleteCalendarEntryUseCase(complianceRepository);

export interface UseComplianceCalendarReturn {
  calendarEntries: MasterComplianceCalendarEntity[];
  groupedEntries: GroupedCalendarEntries;
  loading: boolean;
  error: Error | null;
  fetchCalendarEntries: (filters?: {
    compliance_type_id?: number;
    period?: string;
    is_active?: boolean;
  }) => Promise<void>;
  fetchGroupedEntries: () => Promise<void>;
  getCalendarEntry: (id: number) => Promise<MasterComplianceCalendarEntity>;
  createCalendarEntry: (data: Partial<MasterComplianceCalendarEntity>) => Promise<MasterComplianceCalendarEntity>;
  updateCalendarEntry: (id: number, data: Partial<MasterComplianceCalendarEntity>) => Promise<MasterComplianceCalendarEntity>;
  deleteCalendarEntry: (id: number) => Promise<void>;
}

export const useComplianceCalendar = (): UseComplianceCalendarReturn => {
  const [calendarEntries, setCalendarEntries] = useState<MasterComplianceCalendarEntity[]>([]);
  const [groupedEntries, setGroupedEntries] = useState<GroupedCalendarEntries>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCalendarEntries = useCallback(
    async (filters?: {
      compliance_type_id?: number;
      period?: string;
      is_active?: boolean;
    }) => {
      try {
        setLoading(true);
        setError(null);
        const result = await getCalendarEntriesUseCase.execute(filters);
        setCalendarEntries(result);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to fetch calendar entries")
        );
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchGroupedEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCalendarEntriesGroupedUseCase.execute();
      setGroupedEntries(result);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch grouped calendar entries")
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCalendarEntry = useCallback(async (id: number) => {
    try {
      setError(null);
      return await complianceRepository.getCalendarEntryById(id);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch calendar entry")
      );
      throw err;
    }
  }, []);

  const createCalendarEntry = useCallback(
    async (data: Partial<MasterComplianceCalendarEntity>) => {
      try {
        setError(null);
        const result = await createCalendarEntryUseCase.execute(data);
        // Refresh grouped entries
        await fetchGroupedEntries();
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to create calendar entry")
        );
        throw err;
      }
    },
    [fetchGroupedEntries]
  );

  const updateCalendarEntry = useCallback(
    async (id: number, data: Partial<MasterComplianceCalendarEntity>) => {
      try {
        setError(null);
        const result = await updateCalendarEntryUseCase.execute(id, data);
        // Refresh grouped entries
        await fetchGroupedEntries();
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to update calendar entry")
        );
        throw err;
      }
    },
    [fetchGroupedEntries]
  );

  const deleteCalendarEntry = useCallback(
    async (id: number) => {
      try {
        setError(null);
        await deleteCalendarEntryUseCase.execute(id);
        // Refresh grouped entries
        await fetchGroupedEntries();
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to delete calendar entry")
        );
        throw err;
      }
    },
    [fetchGroupedEntries]
  );

  return {
    calendarEntries,
    groupedEntries,
    loading,
    error,
    fetchCalendarEntries,
    fetchGroupedEntries,
    getCalendarEntry,
    createCalendarEntry,
    updateCalendarEntry,
    deleteCalendarEntry,
  };
};

